import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, CreditCard, Calendar, User, Mail, Building } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const SuperadminCustomerSubscription = () => {
  const { customerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadData();
  }, [customerId]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/superadmin/customer/${customerId}/subscription`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setData(response.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-600 dark:text-gray-400">Kunde nicht gefunden</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { customer, subscription } = data;
  const hasSubscription = !!subscription;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
      {/* Customer Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <User className="h-5 w-5" />
            Kundeninformationen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Building className="h-4 w-4 mt-0.5 text-gray-500" />
              <div>
                <p className="font-medium">Firma</p>
                <p className="text-gray-600 dark:text-gray-400">{customer.company_name}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 text-gray-500" />
              <div>
                <p className="font-medium">E-Mail</p>
                <p className="text-gray-600 dark:text-gray-400">{customer.contact_email}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 mt-0.5 text-gray-500" />
              <div>
                <p className="font-medium">Kundennummer</p>
                <p className="text-gray-600 dark:text-gray-400">{customer.customer_number}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-gray-500" />
              <div>
                <p className="font-medium">Status</p>
                <p className="text-gray-600 dark:text-gray-400">
                  {customer.status === 'active' ? 'Aktiv' : customer.status === 'pending' ? 'Ausstehend' : 'Inaktiv'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Abonnement-Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!hasSubscription ? (
            <div className="text-center py-8">
              <XCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Kein aktives Abonnement</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">AllFlowX Basic</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {subscription.status === 'trialing' && `Trial (${subscription.trial_days_remaining} Tage verbleibend)`}
                      {subscription.status === 'active' && 'Aktiv'}
                      {subscription.status === 'canceled' && 'Gekündigt'}
                      {subscription.status === 'past_due' && 'Zahlung ausstehend'}
                    </p>
                  </div>
                  <div>
                    {subscription.status === 'trialing' && (
                      <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 text-sm rounded-full">
                        Trial
                      </span>
                    )}
                    {subscription.status === 'active' && (
                      <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 text-sm rounded-full">
                        Aktiv
                      </span>
                    )}
                    {subscription.status === 'canceled' && (
                      <span className="px-3 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100 text-sm rounded-full">
                        Gekündigt
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium mb-1">Erstellt am:</p>
                    <p className="text-gray-600 dark:text-gray-400">
                      {new Date(subscription.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>

                  {subscription.trial_ends_at && (
                    <div>
                      <p className="font-medium mb-1">Trial endet:</p>
                      <p className="text-gray-600 dark:text-gray-400">
                        {new Date(subscription.trial_ends_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="font-medium mb-1">Coupon angewendet:</p>
                    <p className="text-gray-600 dark:text-gray-400">
                      {subscription.coupon_applied ? 'Ja (2. Monat)' : 'Nein'}
                    </p>
                  </div>

                  {subscription.cancel_at_period_end && (
                    <div>
                      <p className="font-medium mb-1">Kündigung geplant:</p>
                      <p className="text-gray-600 dark:text-gray-400">
                        {new Date(subscription.current_period_end).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
